'use client';

import { useEffect } from 'react';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  // Close menu on escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-menu-panel]')) return;
      onClose();
    };

    // Small delay to prevent immediate close on open
    const timeout = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeout);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Backdrop - Fondo oscuro sólido */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Panel - Diseño moderno full-screen con fondo sólido */}
      <div
        data-menu-panel
        className={`fixed inset-y-0 right-0 w-full max-w-sm bg-white z-50 md:hidden transform transition-transform duration-300 ease-out shadow-2xl ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header del Menú - Diseño moderno */}
          <div className="flex items-center justify-between px-6 py-5 bg-white border-b border-gray-200 shadow-sm">
            <div className="flex gap-3 items-center">
              <div className="w-12 h-12">
                <img 
                  src="/AbbiAI.png" 
                  alt="AbbiAI Logo" 
                  className="h-full w-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <h2 className="text-xl font-bold text-darkbrown">
                  AbbiAI
                </h2>
                <span className="text-xs text-gray-500 italic">IA con Propósito</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-12 h-12 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition-colors min-h-[48px] min-w-[48px] shadow-sm border border-gray-200"
              aria-label="Close menu"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu Items - Diseño moderno con iconos y cards */}
          <nav className="flex-1 overflow-y-auto py-6 px-4 bg-gray-50">
            <div className="flex flex-col gap-3">
              <a
                href="/"
                onClick={onClose}
                className="group px-5 py-4 text-lg font-semibold text-gray-800 bg-white hover:bg-gray-50 active:bg-gray-100 transition-all rounded-xl border-2 border-transparent hover:border-gray-200 hover:shadow-lg min-h-[64px] flex items-center gap-4 shadow-sm"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-lightbrown group-hover:text-white transition-all duration-200">
                  <svg className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                </div>
                <span className="flex-1">Inicio</span>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </a>
              
              <a
                href="/pricing"
                onClick={onClose}
                className="group px-5 py-4 text-lg font-semibold text-gray-800 bg-white hover:bg-gray-50 active:bg-gray-100 transition-all rounded-xl border-2 border-transparent hover:border-gray-200 hover:shadow-lg min-h-[64px] flex items-center gap-4 shadow-sm"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-lightbrown group-hover:text-white transition-all duration-200">
                  <svg className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="flex-1">Precios</span>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </a>
              
              <a
                href="/contact"
                onClick={onClose}
                className="group px-5 py-4 text-lg font-semibold text-gray-800 bg-white hover:bg-gray-50 active:bg-gray-100 transition-all rounded-xl border-2 border-transparent hover:border-gray-200 hover:shadow-lg min-h-[64px] flex items-center gap-4 shadow-sm"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-gray-100 group-hover:bg-lightbrown group-hover:text-white transition-all duration-200">
                  <svg className="w-6 h-6 text-gray-700 group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <span className="flex-1">Contáctanos</span>
                <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </nav>

          {/* Footer del Menú - CTA Button con diseño moderno */}
          <div className="p-5 bg-white border-t border-gray-200 shadow-lg">
            <a
              href="https://app.abbiai.it/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className="w-full bg-lightbrown text-white rounded-xl px-6 py-4 font-bold text-lg text-center block hover:bg-brown active:bg-darkbrown2 transition-all min-h-[56px] flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Iniciar App
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
